import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { HomeActions } from "../../../Services/_actions/home.action";
import { useSnackBar } from "../../FlowBuilder/Common/SnackBar/index.tsx";
import { ContactData } from "./AccountCreate.tsx";
import ConfirmBox from "../../FlowBuilder/Common/ConfirmBox/index.tsx";

type Props = {
  account?: any;
  getContacts: () => ContactData[];
  refreshAccountData?: () => void;
  validateContacts: () => boolean;
  organisationIdOverride?: string;
  organisationNameOverride?: string;
  entityLabel?: string;
  entityNameLabel?: string;
  variant?: "page" | "embedded";
  onCancel?: () => void;
  onCreateSuccess?: (accountData: any) => void | Promise<void>;
  hideActions?: boolean;
};

export type NewAccountFormHandle = {
  submit: () => Promise<void>;
};

const NewAccountForm = forwardRef<NewAccountFormHandle, Props>(({
  account: accountProp,
  getContacts,
  refreshAccountData,
  validateContacts,
  organisationIdOverride,
  organisationNameOverride,
  entityLabel = "Account",
  entityNameLabel = "Account Name",
  variant = "page",
  onCancel,
  onCreateSuccess,
  hideActions = false,
}, ref) => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const organisationId = organisationIdOverride || searchParams.get("orgId") || "";
  const organisationName = organisationNameOverride || searchParams.get("orgName") || "";
  const passedAccount = accountProp || location.state?.account;
  const accountId = passedAccount?.account_id || searchParams.get("accountId") || "";
  const [organisationDisplayName, setOrganisationDisplayName] = useState<string>(organisationName);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false); // state for confirmation dialog


  useEffect(() => {
    if (passedAccount?.organisation?.name) {
      setOrganisationDisplayName(passedAccount.organisation.name);
    } else if (typeof passedAccount?.organisation === "string") {
      // organisation may be an id string from API detail payloads
      setOrganisationDisplayName((prev) => prev || organisationName);
    }
  }, [passedAccount, organisationName]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    reference_name: "",
    reference_phone: "",
    reference_email: "",
    comments: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const constantActions = HomeActions();

  useEffect(() => {
    if (passedAccount) {
      setFormData({
        name: passedAccount.name || "",
        email: passedAccount.email || "",
        phone: passedAccount.phone || "",
        address: passedAccount.address || "",
        reference_name: passedAccount.reference_name || "",
        reference_phone: passedAccount.reference_phone || "",
        reference_email: passedAccount.reference_email || "",
        comments: passedAccount.comments || "",
      });
    }
  }, [passedAccount]);

  const validate = () => {
    const errs: { [key: string]: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10,13}$/;

    if (!formData.name.trim()) {
      errs.name = `${entityNameLabel} is required`;
    }
    if (formData.email && !emailRegex.test(formData.email)) {
      errs.email = "Invalid email";
    }
    if (formData.phone && !phoneRegex.test(formData.phone.replace(/\D/g, ""))) {
      errs.phone = "Phone must be 10 to 13 digits";
    }
    if (formData.reference_email && !emailRegex.test(formData.reference_email)) {
      errs.reference_email = "Invalid reference email";
    }
    if (formData.reference_phone && !phoneRegex.test(formData.reference_phone)) {
      errs.reference_phone = "Reference phone must be 10 to 13 digits";
    }

    return errs;
  };

  const handleChange = (field: string, value: string) => {
    if (field.includes("phone")) {
      value = value.replace(/\D/g, "");
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // const handleSubmit = async () => {
  //   const validationErrors = validate();
  //   const contactValid = validateContacts();

  //   if (Object.keys(validationErrors).length > 0 || !contactValid) {
  //     setErrors(validationErrors);
  //     return;
  //   }
  //   if (Object.keys(validationErrors).length > 0) {
  //     setErrors(validationErrors);
  //     return;
  //   }

  //   // if (!accountId) {
  //   //   setIsConfirmDialogOpen(true);
  //   //   return;
  //   // }

  //   const payload = accountId
  //     ? {
  //       ...passedAccount,
  //       ...formData,
  //       organisation: passedAccount?.organisation?.organisation_id || organisationId,
  //       contacts: getContacts(),

  //     }
  //     : {
  //       name: formData.name,
  //       organisation: organisationId,
  //       contacts: getContacts(),
  //     };
  //   try {
  //     const res = accountId
  //       ? await constantActions.updateAccount(accountId, payload)
  //       : await constantActions.createAccount(payload);

  //     if (res?.status === 200) {
  //       showSnackBar(accountId ? "Account Updated" : "Account Created", "success");

  //       // ✅ Re-fetch updated data if this is an update
  //       if (accountId && refreshAccountData) {
  //         await refreshAccountData();
  //       }

  //       navigate(-1);
  //     } else {
  //       showSnackBar("API failed", "error");
  //     }
  //   } catch (error) {
  //     console.error("Submission failed:", error);
  //   }
  // };

  const handleSubmit = async () => {
    const validationErrors = validate();
    const contactValid = validateContacts();

    if (Object.keys(validationErrors).length > 0 || !contactValid) {
      setErrors(validationErrors);
      return;
    }

    const contacts = getContacts();

    if (accountId) {
      // 🔁 Update Account
      const organisationFromAccount =
        passedAccount?.organisation?.organisation_id ||
        (typeof passedAccount?.organisation === 'string'
          ? passedAccount.organisation
          : '') ||
        organisationId;
      const payload = {
        ...passedAccount,
        ...formData,
        organisation: organisationFromAccount,
        contacts,
      };

      try {
        const res = await constantActions.updateAccount(accountId, payload);
        if (res?.status === 200) {
          showSnackBar("Account Updated", "success");

          if (refreshAccountData) {
            await refreshAccountData();
          }

          navigate(-1);
        } else {
          showSnackBar("Update failed", "error");
        }
      } catch (error) {
        console.error("Update failed:", error);
        showSnackBar("An error occurred during update", "error");
      }
    } else {
      // Create Account / Business Function
      const payload = {
        ...formData,
        name: formData.name,
        organisation: organisationId,
        contacts,
      };

      try {
        const res = await constantActions.createAccount(payload);
        if (res?.status === 200) {
          showSnackBar(`${entityLabel} Created`, "success");
          if (onCreateSuccess) {
            const accountData = res.data?.data ?? res.data;
            await onCreateSuccess(accountData);
          } else {
            setIsConfirmDialogOpen(true);
          }
        } else {
          showSnackBar("Creation failed", "error");
        }
      } catch (error) {
        console.error("Creation failed:", error);
        showSnackBar("An error occurred during creation", "error");
      }
    }
  };

  useImperativeHandle(ref, () => ({
    submit: handleSubmit,
  }));

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate(-1);
  };

  const isEmbedded = variant === "embedded";

  const handleConfirmation = async (isConfirmed: boolean) => {
    setIsConfirmDialogOpen(false);
    if (isConfirmed) {
      // navigate('/r/management/create-campaign');
      navigate('/r/management/create-campaign', {
        state: {
          accountId,
          organisationId,
        },
      });

    } else {
      navigate(-1);
    }
  };

  return (
    <Box sx={isEmbedded ? undefined : { maxWidth: 600, mx: "auto", mt: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" mb={3} textAlign="center">
            {accountId ? `Edit ${entityLabel}` : `Create ${entityLabel}`}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={
                  <Typography component="span">
                    {entityNameLabel}<span style={{ color: 'red' }}>*</span>
                  </Typography>
                }
                variant="outlined"
                fullWidth
                size="small"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Organisation"
                variant="outlined"
                fullWidth
                size="small"
                value={organisationDisplayName}
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                inputProps={{
                  maxLength: 13,
                  inputMode: "numeric",
                  pattern: "[0-9]*"
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address"
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Reference Name"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.reference_name}
                onChange={(e) => handleChange("reference_name", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Reference Phone"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.reference_phone}
                onChange={(e) => handleChange("reference_phone", e.target.value)}
                error={!!errors.reference_phone}
                helperText={errors.reference_phone}
                inputProps={{
                  maxLength: 13,
                  inputMode: "numeric",
                  pattern: "[0-9]*"
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Reference Email"
                variant="outlined"
                fullWidth
                size="small"
                value={formData.reference_email}
                onChange={(e) => handleChange("reference_email", e.target.value)}
                error={!!errors.reference_email}
                helperText={errors.reference_email}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Comments"
                variant="outlined"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={formData.comments}
                onChange={(e) => handleChange("comments", e.target.value)}
              />
            </Grid>
          </Grid>

          {!hideActions ? (
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button size="small" variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleSubmit}>
                Save
              </Button>
            </Box>
          ) : null}
        </CardContent>
      </Card>
      {!onCreateSuccess ? (
        <ConfirmBox
          open={isConfirmDialogOpen}
          onClose={handleConfirmation}
          options={{
            title: 'Campaign Create',
            text: 'Do you want to create a Campaign?',
            confirmButtonText: 'yes',
            cancelButtonText: 'Cancel',
            confirmButtonColor: 'primary',
            cancelButtonColor: 'default',
          }}
        />
      ) : null}
    </Box>
  );
});

NewAccountForm.displayName = "NewAccountForm";

export default NewAccountForm;
